import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useNotification } from '../context/NotificationContext';

interface ActiveShiftViewProps {
  job: any;
  visit: any;
  userType: 'caregiver' | 'customer';
  onShiftEnd?: () => void;
  compact?: boolean; // New prop to control display style
  showSafetyChecks?: boolean; // Control whether to show safety checks
  sharedPin?: string; // Pin shared from chat
  onSafetyStatusChange?: (status: string) => void;
}

const ActiveShiftView: React.FC<ActiveShiftViewProps> = ({ job, visit, userType, onShiftEnd, compact = false, showSafetyChecks = true, sharedPin, onSafetyStatusChange }) => {
  const { socket } = useNotification();
  const [safetyStatus, setSafetyStatus] = useState<any>(null);
  const [pinInput, setPinInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // Sync sharedPin prop to local state
  useEffect(() => {
    if (sharedPin) {
        setPinInput(sharedPin);
        // Optional: auto-verify or focus? 
        // For safety, let them click verify manually, but we fill it in.
    }
  }, [sharedPin]);

  // Poll for safety status every 30 seconds
  useEffect(() => {
    if (!showSafetyChecks) return;
    
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/api/shifts/${job._id}/visit/${visit._id}/safety-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSafetyStatus(res.data);
        if (onSafetyStatusChange && res.data?.status) {
            onSafetyStatusChange(res.data.status);
        }
      } catch (err) {
        console.error(err);
      }
    };

    checkStatus();
    const interval = setInterval(() => {
      if (!document.hidden) checkStatus();
    }, 5000); // 5s polling only if active
    return () => clearInterval(interval);
  }, [job._id, visit._id, showSafetyChecks]);

  // Socket listeners for real-time safety updates
  useEffect(() => {
    if (!socket || !job._id || !showSafetyChecks) return;

    // Handler to re-fetch status
    const refreshStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/shifts/${job._id}/visit/${visit._id}/safety-status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSafetyStatus(res.data);
            if (onSafetyStatusChange && res.data?.status) {
                onSafetyStatusChange(res.data.status);
            }
        } catch (err) { console.error(err); }
    };

    socket.on('safety_check_triggered', (data: any) => {
        if (data.visitId === visit._id) refreshStatus();
    });
    
    socket.on('safety_check_verified', (data: any) => {
        if (data.visitId === visit._id) refreshStatus();
    });

    socket.on('safety_check_failed', (data: any) => {
        if (data.visitId === visit._id) refreshStatus();
    });

    return () => {
        socket.off('safety_check_triggered');
        socket.off('safety_check_verified');
        socket.off('safety_check_failed');
    };
  }, [socket, job._id, visit._id, showSafetyChecks]);

  // Calculate time remaining for safety check
  useEffect(() => {
    if (!showSafetyChecks) { setTimeRemaining(null); return; }

    const targetTimeSource = safetyStatus?.checkTime || visit?.safetyCheck?.nextCheckTime;
    
    if (!targetTimeSource) {
      if (safetyStatus?.status === 'normal' || !safetyStatus) {
         setTimeRemaining(null);
      }
      return;
    }

    const updateTimer = () => {
      const checkTime = new Date(targetTimeSource).getTime();
      const now = Date.now();
      // Server timeout is 5 minutes from checkTime
      const deadline = checkTime + (5 * 60 * 1000); 
      const remaining = Math.max(0, deadline - now);
      
      setTimeRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [visit?.safetyCheck?.nextCheckTime, safetyStatus, showSafetyChecks]);

  const formatTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEndShift = async () => {
    if (!window.confirm('Are you sure you want to end the shift?')) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/shifts/${job._id}/shift/end`, 
        { visitId: visit._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onShiftEnd) onShiftEnd();
    } catch (err) {
      alert('Error ending shift');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPin = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/shifts/${job._id}/visit/${visit._id}/verify-pin`,
        { pin: pinInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPinInput('');
      setSafetyStatus({ status: 'normal' }); // Optimistic update
      alert('Safety Check Passed');
    } catch (err) {
      alert('Incorrect PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerSafetyCheck = async () => {
    if (!window.confirm("Are you sure you want to trigger a manual safety check?")) return;
    try {
        setLoading(true);
        const token = localStorage.getItem('token');
        await axios.post(`${API_BASE_URL}/api/shifts/${job._id}/visit/${visit._id}/trigger-safety-check`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        alert('Safety check triggered. A PIN will appear shortly.');
    } catch (err: any) {
        alert(err.response?.data?.message || 'Failed to trigger check');
    } finally {
        setLoading(false);
    }
  };

  // Emergency numbers
  const getEmergencyContacts = () => {
    const contacts = [
      { 
        name: 'National Emergency', 
        number: '999', 
        description: 'Police, Fire, Ambulance',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-100',
        textColor: 'text-red-700',
        badgeColor: 'bg-red-600',
        icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
      },
      { 
        name: 'Proyas Hotline', 
        number: '16999', 
        description: '24/7 Support Center',
        bgColor: 'bg-primary-50',
        borderColor: 'border-primary-100',
        textColor: 'text-primary-700',
        badgeColor: 'bg-primary-600',
        icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
      }
    ];

    // Customer Emergency Contact (Relative/Next of Kin)
    if (job.customer?.emergencyContact?.phone) {
      contacts.push({
        name: job.customer.emergencyContact.name || 'Emergency Contact',
        number: job.customer.emergencyContact.phone,
        description: job.customer.emergencyContact.relationship ? `${job.customer.emergencyContact.relationship}` : 'Customer Emergency Contact',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-100',
        textColor: 'text-orange-700',
        badgeColor: 'bg-orange-600',
        icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
      });
    }

    // Preferred Hospital
    if (job.customer?.preferredHospital?.phone) {
      contacts.push({
        name: job.customer.preferredHospital.name || 'Hospital',
        number: job.customer.preferredHospital.phone,
        description: job.customer.preferredHospital.address ? job.customer.preferredHospital.address.substring(0, 30) + (job.customer.preferredHospital.address.length > 30 ? '...' : '') : 'Preferred Hospital',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-100',
        textColor: 'text-blue-700',
        badgeColor: 'bg-blue-600',
        icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
      });
    }

    return contacts;
  };

  const emergencyContacts = getEmergencyContacts();

  if (safetyStatus?.emergency) {
    return (
      <div className="fixed inset-0 bg-red-600 z-50 flex flex-col items-center justify-center text-white p-6 animate-pulse">
        <h1 className="text-4xl font-bold mb-4">EMERGENCY ALERT</h1>
        <p className="text-xl mb-8 text-center">Safety Check Failed. Emergency Services are being contacted.</p>
        <div className="flex gap-4 flex-wrap justify-center">
          {emergencyContacts.map(c => (
             <a href={`tel:${c.number}`} key={c.number} className="bg-white text-red-600 px-6 py-3 rounded-full font-bold text-lg">
               Call {c.name} ({c.number})
             </a>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`${compact ? '' : 'bg-white rounded-xl shadow-lg border-2 border-primary-200'} overflow-hidden`}>
      {/* Header - Only show if not compact */}
      {!compact && (
      <div className="bg-primary-600 p-4 text-white flex justify-between items-center">
        <div>
          <h2 className="font-bold text-xl">Ongoing Visit</h2>
          <p className="opacity-90 text-sm">Started at: {new Date(visit.actualStartTime).toLocaleTimeString()}</p>
        </div>
        <div className="bg-green-400 w-3 h-3 rounded-full animate-ping"></div>
      </div>
      )}

      <div className={`${compact ? 'p-0' : 'p-6 space-y-6'}`}>
        {/* Compact Start Time */}
        {compact && (
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-100 flex justify-between items-center mb-4">
                <span className="text-sm text-gray-500">Started at</span>
                <span className="font-mono font-bold text-gray-700">{new Date(visit.actualStartTime).toLocaleTimeString()}</span>
            </div>
        )}
        
        <div className={compact ? 'px-4 sm:px-6 pb-6 space-y-6' : ''}>
        {/* Safety Check UI */}
        {showSafetyChecks && (safetyStatus?.status === 'safety_check_triggered' || safetyStatus?.status === 'safety_check_active') && (userType === 'customer' ? (
          <div className="bg-yellow-50 border-2 border-yellow-400 p-3 sm:p-4 rounded-lg text-center">
            <h3 className="text-yellow-800 font-bold text-base sm:text-lg mb-2">Safety Check Required</h3>
            <p className="text-xs sm:text-sm mb-3 sm:mb-4">Please show this PIN to the caregiver:</p>
            <div className="text-3xl sm:text-4xl font-mono font-bold tracking-widest text-primary-700 bg-white p-2 rounded border border-gray-200 inline-block">
              {safetyStatus.pin}
            </div>
            {safetyStatus?.status === 'safety_check_active' && (
              <p className="text-xs text-gray-600 mt-2 sm:mt-3">Waiting for caregiver to verify...</p>
            )}
          </div>
        ) : (
          <div className="bg-red-50 border-2 border-red-400 p-3 sm:p-4 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-red-800 font-bold text-base sm:text-lg">⚠ Safety Check Needed</h3>
              {timeRemaining !== null && timeRemaining > 0 && (
                <div className="flex items-center gap-1 bg-red-200 px-2 py-1 rounded text-red-900">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-mono font-bold text-xs sm:text-sm">{formatTime(timeRemaining)}</span>
                </div>
              )}
            </div>
            <p className="text-xs sm:text-sm mb-3 sm:mb-4">Ask the customer for the PIN code shown on their device.</p>
            <div className="flex flex-col gap-2 w-full max-w-xs mx-auto">
              <label htmlFor="safety-pin-input" className="text-xs font-semibold text-gray-700 mb-1 text-left">Enter Safety PIN</label>
              <input 
                id="safety-pin-input"
                type="text" 
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pinInput}
                onChange={e => setPinInput(e.target.value.replace(/\D/g, ''))}
                className="flex-1 border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 p-4 rounded-xl text-center text-2xl sm:text-3xl font-mono tracking-widest bg-white transition-all outline-none shadow-sm"
                placeholder="Enter 4-digit PIN"
                autoComplete="one-time-code"
              />
              <button 
                onClick={handleVerifyPin}
                disabled={loading || pinInput.length !== 4}
                className="mt-2 bg-red-600 text-white px-4 py-2 rounded-lg font-bold disabled:opacity-50 text-base shadow hover:bg-red-700 transition-colors"
              >
                Verify
              </button>
              <span className="text-xs text-gray-500 mt-1 text-center">Ask the customer for the PIN and enter it here to confirm safety.</span>
            </div>
          </div>
        ))}


        {/* Manual Trigger (Customer Only) */}
        {userType === 'customer' && showSafetyChecks && (!safetyStatus || safetyStatus.status === 'normal') && (
            <div className="flex justify-center my-6">
              <button
                onClick={handleTriggerSafetyCheck}
                disabled={loading}
                className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 font-bold px-6 py-3 rounded-lg shadow-md border-2 border-yellow-500 transition-all text-base disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 8v.01" /></svg>
                Manual Safety Check
              </button>
            </div>
        )}

        {/* Emergency Contacts - 2-column grid */}
        <div className="mt-4 border-t pt-4">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            Emergency & Support
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {emergencyContacts.map((contact, idx) => (
              <a 
                key={idx}
                href={`tel:${contact.number}`}
                className={`flex flex-col items-center sm:flex-row sm:items-center p-2 rounded-lg border ${contact.borderColor} ${contact.bgColor} hover:shadow-sm transition-all gap-2 sm:gap-3 text-center sm:text-left ${emergencyContacts.length === 3 && idx === 2 ? 'col-span-2' : ''}`}
              >
                <div className={`p-1.5 rounded-full ${contact.badgeColor} bg-opacity-10 ${contact.textColor} shrink-0`}>
                  <div className="w-5 h-5 [&>svg]:w-5 [&>svg]:h-5">
                    {contact.icon}
                  </div>
                </div>
                <div>
                  <p className={`font-bold ${contact.textColor} text-xs leading-tight line-clamp-1`}>{contact.name}</p>
                  <p className="text-[10px] text-gray-500 font-mono mt-0.5">{contact.number}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* End Shift Button (Caregiver Only) */}
        {userType === 'caregiver' && (
          <div className="pt-4 border-t">
            <button
              onClick={handleEndShift}
              disabled={loading}
              className="w-full bg-gray-800 text-white py-4 rounded-lg font-bold text-lg hover:bg-gray-900 transition-colors flex justify-center items-center gap-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              End Shift
            </button>
            <p className="text-center text-xs text-gray-500 mt-2">
              This will record the end time and calculate your hours.
            </p>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default ActiveShiftView;
